import {Text} from '@rdcomponents/text/text';
import {Alert, Flex, Tooltip} from 'antd';
import {memo} from 'react';

import {Locale} from '../../../../../../provider/locale/locale';
import {formatAddress} from '../../../../../../service/address/address-helper';
import {useReviewLogsList} from '../../../../../../service/reviews/reviews-logs';
import {useGetRequiredGroups} from '../../../../../../service/reviews/reviews-tags';
import {classNames} from '../../../../../../util/css';
import {useReviewActivityHook} from '../../../review-activity-provider/review-activity-provider';
import {NewReviewAnswers} from '../../../review-answers/new-review-answers';
import {ReviewStatus} from '../../../review-status/review-status';
import {useReviewStateHook} from '../../../reviews-state/reviews-state-provider';
import {TagList} from '../../../tag-list/tag-list';
import {NewReviewNotes} from '../../new-review-notes/new-review-notes';
import {ReviewDataWithCatalogType} from '../review-list-type';

import {NewReviewComment} from './new-review-comment/new-review-comment';
import {ReviewHeader} from './review-header/review-header';
import {getMissingRequiredGroups} from './review-helper';
import * as styles from './new-review.scss';

type PropsType = {
    className?: string;
    reviewData: ReviewDataWithCatalogType;
};

// eslint-disable-next-line complexity
export const NewReview = memo(function ReviewInner(props: PropsType): JSX.Element {
    const {className, reviewData} = props;

    const {refreshReview, reviewsInfo, suggestionMap, refreshSuggestions, updateReview} = useReviewStateHook();

    const suggestion = suggestionMap[reviewData.id];

    const info = reviewsInfo?.results.find((reviewInfo) => reviewInfo.id === reviewData.id) || null;

    const {id, catalog, company, deletedAt, responsibleUser, brand, comment, processStatus, tags} = reviewData;

    const isReviewDeleted = Boolean(deletedAt);

    const {isHistoryVisible} = useReviewActivityHook();
    const {refetch: loadLogs} = useReviewLogsList(id, isHistoryVisible);
    const {data: requiredGroups} = useGetRequiredGroups();
    const tagGroups = tags?.flatMap((tag) => (tag.folderId ? [tag.folderId] : []));
    const uniqueRequiredGroup = getMissingRequiredGroups(requiredGroups || [], tagGroups || []);
    const isGptSuggestSupported = catalog?.gpt_suggest_supported;

    const notesCount = info?.notesCount || 0;

    return (
        <div className={classNames(styles.Review, className)}>
            <Flex vertical>
                <div className={styles.Review_generalWrapper}>
                    <ReviewHeader info={info} isReviewDeleted={isReviewDeleted} reviewData={reviewData} />
                    <ReviewStatus
                        className={styles.Review_status}
                        onChange={loadLogs}
                        processStatus={processStatus}
                        reviewId={id}
                        updateReview={updateReview}
                    />
                    {!company ? (
                        <Text bolder lighter>
                            {brand?.name}
                        </Text>
                    ) : (
                        <Flex gap={4} wrap="wrap">
                            <Text bolder lighter>
                                {company?.name}
                            </Text>
                            <div className={styles.Review_catalogCompanyLine}>
                                <Text lighter>{formatAddress(company?.address || null)}</Text>
                                {company?.code && (
                                    <Tooltip
                                        placement="bottom"
                                        title={<Locale stringKey="REVIEWS__REVIEW__COMPANY_CODE__HINT" />}
                                    >
                                        <Text secondary>{company.code}</Text>
                                    </Tooltip>
                                )}
                            </div>
                        </Flex>
                    )}

                    <NewReviewComment comment={comment} reviewId={id} />
                    <TagList
                        deletedAt={deletedAt}
                        responsibleUser={responsibleUser}
                        reviewId={id}
                        tags={tags}
                        updateReview={updateReview}
                    />
                    {uniqueRequiredGroup && uniqueRequiredGroup?.length > 0 && (
                        <Alert
                            className={styles.Review_requiredTagGroupsAlert}
                            message={
                                <Text
                                    secondary
                                    small
                                    stringKey="REVIEW__REQUIRED_GROUP__ALERT"
                                    valueMap={{
                                        tagsGroup: uniqueRequiredGroup?.map((group, index, array) => (
                                            <Text bolder key={group.id} small>
                                                {group.title}
                                                {index < array.length - 1 && ', '}
                                            </Text>
                                        )),
                                    }}
                                />
                            }
                            showIcon
                            type="warning"
                        />
                    )}

                    {notesCount > 0 && <NewReviewNotes notesCount={notesCount} reviewId={id} />}
                </div>
                <div className={styles.Review_answerWrapper}>
                    <NewReviewAnswers
                        isCollapsedAnswers
                        isGptSuggestSupported={isGptSuggestSupported}
                        refreshReview={refreshReview}
                        refreshSuggestions={refreshSuggestions}
                        review={reviewData}
                        suggestion={suggestion}
                    />
                </div>
            </Flex>
        </div>
    );
});
